import { useAuthContextV2 } from "../../../../auth-state";
import { PortalTemplate } from "../../../template/portal/default-template";
import { ProcManagementR } from "../route";
import { TtzkPortalR } from "../../ttzk-portal/route";
import React, { useEffect } from "react";
import {
  FormContainer,
  FormContainerItem,
  RadioButton,
  Spinner,
  PrimaryButton,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import { toast } from "react-toastify";
import apiClient from "../../../../application/apis/api-client";
import {
  isNotFoundError,
  reportProcManagementRelatedError,
} from "../../../../application/domain/proc-management/util/error";

type PortalConfig = {
  id: string;
  organizationId: string;
  searchConfig: {
    searchMode: string;
  };
  createdAt: string;
  updatedAt: string;
};

type SearchConfigRequest = {
  searchConfig: {
    searchMode: string;
  };
};

export const ProcManagementConfigEdit = () => {
  const { auth } = useAuthContextV2();
  const orgId = auth.data?.orgId;
  const orgSlug = auth.data?.orgSlug;

  const [serverConfig, setServerConfig] = React.useState<PortalConfig | null>(
    null,
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedSearchMode, setSelectedSearchMode] =
    React.useState<string>("proc-management");
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const isConfigChanged =
    serverConfig?.searchConfig?.searchMode !== selectedSearchMode;

  const fetchConfigByOrgId = async (orgId: string) => {
    setIsLoading(true);
    try {
      const res =
        await apiClient.procManagement.findProcManagementConfig(orgId);
      const config = res.data as PortalConfig;
      setServerConfig(config);
      setSelectedSearchMode(
        config?.searchConfig?.searchMode ?? "proc-management",
      );
    } catch (error) {
      if (!isNotFoundError(error)) {
        reportProcManagementRelatedError(error);
        const message = error.response?.data?.msg ?? error.message;
        toast.error(
          `設定の取得に失敗しました。 organizationId: ${orgId}, error message: ${message}`,
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSearchMode(e.target.value);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (orgId) {
        const configToUpdate: SearchConfigRequest = {
          searchConfig: {
            searchMode: selectedSearchMode,
          },
        };
        const { data } =
          await apiClient.procManagement.upsertProcManagementConfig(
            configToUpdate,
            orgId,
          );
        setServerConfig(data as PortalConfig);
        toast.success("設定を更新しました。");
      }
    } catch (error) {
      reportProcManagementRelatedError(error);
      const message = error.response?.data?.msg ?? error.message;
      toast.error(
        `設定の更新に失敗しました。 organizationId: ${orgId}, error message: ${message}`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (orgId) {
      fetchConfigByOrgId(orgId);
    }
  }, [orgId]);

  return (
    <PortalTemplate
      breadcrumbs={[
        { to: TtzkPortalR.Home.link({}), title: "Graffer ポータル" },
        {
          to: ProcManagementR.ConfigEdit.link({}),
          title: "検索設定",
        },
      ]}
    >
      {typeof orgId === "string" && typeof orgSlug === "string" && (
        <div className={"content"}>
          <h1>検索設定</h1>
          <hr />
          <FormContainer>
            <RBACHidden
              requirePermissions={"proc_management.search_config.read"}
            >
              {isConfigChanged && (
                <FormContainerItem>
                  <WarningBanner message="設定が変更されています。変更を反映するには保存ボタンを押してください。" />
                </FormContainerItem>
              )}
              <FormContainerItem>
                <RadioButton
                  description="検索対象の手続きを選択して下さい"
                  label="手続きマスタの検索設定"
                  onChange={(e) => onChange(e)}
                  options={[
                    {
                      name: "手続きマスタの手続きから検索（デフォルト）",
                      value: "proc-management",
                    },
                    {
                      name: "スマート申請の手続きから検索",
                      value: "smart-apply",
                    },
                    {
                      description:
                        "手続きマスタの手続きから検索して、結果がない場合にスマート申請の手続きから検索",
                      name: "ハイブリッド検索",
                      value: "hybrid",
                    },
                  ]}
                  value={selectedSearchMode}
                />
              </FormContainerItem>
              <FormContainerItem>
                <PrimaryButton
                  onClick={handleSave}
                  isLoading={isSaving}
                  disabled={isSaving || !isConfigChanged}
                  text="保存"
                />
              </FormContainerItem>
            </RBACHidden>
          </FormContainer>
          <Spinner loading={isLoading} />
        </div>
      )}
    </PortalTemplate>
  );
};
